import type { UniqueIdentifier } from '@dnd-kit/core';
import { isEqual } from 'lodash-es';
import {
  FC,
  RefAttributes,
  useCallback,
  useEffect,
  useMemo,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';

import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';
import { UpdateJobLineItemRequestData } from '@payaca/store/jobContent/jobContentTypes';

import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { JobLineItem, JobLineItemBase } from '@payaca/types/jobContentTypes';

import Checkbox from '@payaca/components/checkbox/Checkbox';
import RadioButton from '@payaca/components/radioButton/RadioButton';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import JobLineItemCard from '../jobLineItemCard/JobLineItemCard';

import { getJobLineItem } from '../../../utils/stateAccessors';
import {
  getRequiredSettingsType,
  RequiredSettingsType,
} from '../requiredSettingsDropdown/RequiredSettingsDropdown';

import { useSelector } from '@/api/state';

const getFormStateFromJobLineItem = (jobLineItem?: JobLineItemBase) => {
  if (!jobLineItem) return {};

  return {
    jobLineItemId: jobLineItem.id,
    description: jobLineItem.description,
    reference: jobLineItem.name,
    isMultipleChoice: jobLineItem.isMultipleChoice,
    isOptional: jobLineItem.isOptional,
    isSelected: jobLineItem.isSelected,
    quantity: jobLineItem.quantity,
    price: jobLineItem.price,
    vatAmount: jobLineItem.vatAmount,
    vatIncluded: jobLineItem.vatIncluded,
    isReverseChargeVat: jobLineItem.isReverseChargeVat,
    taxRateId: jobLineItem.taxRateId,
    cisApplies: !!jobLineItem.cisDeductionRate,
    lineItemId: jobLineItem.lineItemId,
    discountPercentage: jobLineItem.discountPercentage,
    discountDescription: jobLineItem.discountDescription,
  };
};

type Props = {
  jobLineItemId: number;
  handleOpenCreateJobLineItemModal?: (
    jobLineItemId: number,
    initialItemData?: Partial<JobLineItemBase>
  ) => void;
  dragHandleId?: UniqueIdentifier;
};

const JobLineItemControl: FC<Props & RefAttributes<HTMLDivElement>> = ({
  jobLineItemId,
  handleOpenCreateJobLineItemModal,
  dragHandleId,
  ...rest
}): JSX.Element | null => {
  const dispatch = useDispatch();

  const jobLineItem: JobLineItem | undefined = useSelector((state) =>
    getJobLineItem(state, jobLineItemId)
  );

  useEffect(() => {
    if (jobLineItemId) {
      dispatch(
        jobContentActions.requestGetJobLineItemAttachmentsForJobLineItem(
          jobLineItemId
        )
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [jobLineItemId]);

  const initialFormState = useMemo(() => {
    return getFormStateFromJobLineItem(jobLineItem);
  }, [jobLineItem]);

  const onJobLineItemUpdateSuccess = useCallback(() => {
    if (!jobLineItem?.jobContentId) return;
    dispatch(
      jobContentActions.requestGetJobContentWithJobGroupsAndJobLineItems(
        jobLineItem.jobContentId
      )
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
  }, [jobLineItem?.jobContentId]);

  const isUpdatingJobLineItem: boolean = useSelector((state) => {
    return state.jobContent.isUpdatingJobLineItem;
  });

  const isFetchingJobLineItem: boolean = useSelector((state) => {
    return (
      state.jobContent.jobLineItems &&
      state.jobContent.jobLineItems[jobLineItemId]?.isFetching
    );
  });

  const requiresUpdateJobLineItem = useCallback(
    (formState: { [key: string]: any }) => {
      if (isFetchingJobLineItem || isUpdatingJobLineItem) return true;
      return !isEqual(formState, getFormStateFromJobLineItem(jobLineItem));
    },
    [isFetchingJobLineItem, isUpdatingJobLineItem, jobLineItem]
  );

  const updateJobLineItem = useCallback(
    (formState: { [key: string]: any }) => {
      const updateJobLineItemRequestData =
        formState as UpdateJobLineItemRequestData;
      if (!updateJobLineItemRequestData.jobLineItemId) return;
      if (requiresUpdateJobLineItem(formState)) {
        dispatch(
          jobContentActions.requestUpdateJobLineItem(
            updateJobLineItemRequestData,
            onJobLineItemUpdateSuccess
          )
        );
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    [onJobLineItemUpdateSuccess, requiresUpdateJobLineItem]
  );

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      _onFieldTouch: (fieldName: string) => void
    ) => {
      const requiredSettingsType = getRequiredSettingsType(formState);

      if (requiredSettingsType === RequiredSettingsType.REQUIRED) {
        return <></>;
      }

      return (
        <div className="mr-2 flex items-center">
          {requiredSettingsType === RequiredSettingsType.OPTIONAL && (
            <Checkbox
              isChecked={formState.isSelected}
              onChange={() => {
                const updateItem = {
                  isSelected: !formState.isSelected,
                };
                onFieldChange(updateItem);
                updateJobLineItem({ ...formState, ...updateItem });
              }}
            />
          )}
          {requiredSettingsType === RequiredSettingsType.MULTIPLE_CHOICE && (
            <RadioButton
              isSelected={formState.isSelected}
              onClick={() => {
                if (formState.isSelected) return;
                const updateItem = {
                  isSelected: !formState.isSelected,
                };
                onFieldChange(updateItem);
                updateJobLineItem({ ...formState, ...updateItem });
              }}
            />
          )}
        </div>
      );
    },
    [updateJobLineItem]
  );

  return (
    <div className="flex w-full min-w-0 items-center" {...rest}>
      {/* Optional/Multiple choice selector */}
      <ValidatedForm
        initialFormState={initialFormState}
        renderFormContents={renderFormContents}
      />

      {/* Job line item card with integrated drag handle */}
      {jobLineItem && (
        <JobLineItemCard
          jobLineItem={jobLineItem}
          onClick={
            handleOpenCreateJobLineItemModal
              ? () => handleOpenCreateJobLineItemModal(jobLineItemId)
              : undefined
          }
          dragHandleId={dragHandleId}
        />
      )}
    </div>
  );
};

export default JobLineItemControl;
